import { MenuVersion } from '@/config/menuRollout';

// Mirrors VerticalMenuBar.vue's inline RC-plan check (`isReplenishmentPackage:
// !!store?.currentPackage?.name?.toLowerCase().includes('replenishment')`)
// rather than introducing a new predicate shape.
export function isReplenishmentPackage(packageName: string | null | undefined): boolean {
  return !!packageName?.toLowerCase().includes('replenishment');
}

// Gates D1 (RC stores mid-first-import land on Today) behind
// VUE_APP_ATLAS_ASSISTANT_TODAY. Atlas's frontend reads its own mirrored var,
// NEXT_PUBLIC_ATLAS_ASSISTANT_TODAY — two separate vars in two separate
// builds, not one shared value, so they must be flipped together by hand and
// can silently drift if only one side is updated.
export function isAssistantTodayEnabled(): boolean {
  return process.env.VUE_APP_ATLAS_ASSISTANT_TODAY === 'true';
}

export const ATLAS_TODAY_PATH = '/assistant/today';
export const ATLAS_ONBOARDING_PATH = '/assistant/onboarding';

// Pure landing decision extracted from Home.vue's watcher so it can be
// unit tested directly instead of via a re-implementation. See
// Home.vue:onMenuVersionOrStoreChanged for the guard rationale.
//
// Returns where `/` should send the store, or null to stay on classic home.
// A store whose data is not ready yet lands on the atlas ONBOARDING page, not
// Today: Today has nothing to show until the first import finishes, while the
// onboarding page tracks the import and moves the merchant on to Today itself
// once it does (atlas GuidedOnboarding -> leaveForToday).
export function homeLandingPath(input: {
  menuVersion: MenuVersion;
  storeId: number | null | undefined;
  passedInstallingFlow: string | undefined;
  routeName: string | null | undefined;
  packageName?: string | null;
  isAthenaFinished?: boolean;
}): string | null {
  const isPassedInstallation = input.storeId != null
    && input.passedInstallingFlow !== 'new';
  const isOnHome = input.routeName === 'home';
  const isV3Landing = input.menuVersion === 3 && isPassedInstallation && isOnHome;

  // "Data not ready" is `isAthenaFinished !== true`, never `=== false`: a real
  // StoreModel's `isAthenaFinished` is `undefined` (never `false`) while the
  // first import runs -- mapData() never assigns it, and checkEtlStatus() only
  // ever sets it `= true`. `isPassedInstallation` gates out the unloaded-store
  // case, and StoreModel.fetch() resolves only after checkEtlStatus(), so a
  // loaded store's value is already settled here. Do not "simplify" this to
  // `=== false` (see atlasEmbedContext.ts for the same trap on the other
  // surface).
  const isDataReady = input.isAthenaFinished === true;

  // US-TODAY-101/D1: an RC (Replenishment Center) store whose first Shopify
  // import is still running also lands on the assistant -- instead of the
  // classic /data-importing wait screen -- once it reaches the normal
  // post-onboarding home landing, on any menu version. /data-importing itself
  // stays directly reachable (e.g. the "View Progress" menu item); this only
  // changes the default landing. Its import is by definition not finished, so
  // it lands on onboarding like any other store whose data is not ready.
  //
  // Requires an EXPLICITLY-resolved onboarding state. `isPassedInstallation`
  // uses the codebase-wide `passedInstallingFlow !== 'new'` idiom (App.vue /
  // store.ts), which also treats `undefined` -- onboarding status not loaded
  // yet -- as "passed". For this landing we hold to `=== 'passed'` so an
  // unresolved state does not redirect before onboarding has actually
  // completed. (The shared `isPassedInstallation` / EPIC-012 `isV3Landing`
  // idiom is intentionally left untouched.)
  const isImportingRCLanding = isPassedInstallation
    && input.passedInstallingFlow === 'passed'
    && isOnHome
    && isReplenishmentPackage(input.packageName)
    && !isDataReady
    && isAssistantTodayEnabled();

  if (!isV3Landing && !isImportingRCLanding) {
    return null;
  }
  return isDataReady ? ATLAS_TODAY_PATH : ATLAS_ONBOARDING_PATH;
}
